import React, { PropsWithoutRef, useEffect, useState } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Menu,
  MenuItem,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Alert,
  Badge,
} from "@mui/material";
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from "@mui/icons-material/Person";
import LogoutIcon from "@mui/icons-material/Logout";
import { useNavigate } from "react-router-dom";
import { SIDEBAR_WIDTH } from "../constants";
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import MenuIcon from "@mui/icons-material/Menu";
import { useGetProfileQuery, useLogoutMutation } from "../service/auth/api";
import { useGetNotificationsQuery, useBulkMarkAsReadMutation } from "../service/notifications/api";

import { useDispatch, useSelector } from "react-redux";
import { setCredentials, logout } from "../slice/authSlice";
import type { RootState } from "../service/index";
import { toast } from "react-toastify";


interface Props {
  handleSidebarToggle: () => void;
}

function Topbar({ handleSidebarToggle }: PropsWithoutRef<Props>) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { data: profileData } = useGetProfileQuery({}, { refetchOnMountOrArgChange: true });
  const authUser = useSelector((state: RootState) => state.auth.user);
  const [logoutApi] = useLogoutMutation();
  
  const { data: notificationsData, isLoading: notificationsLoading, error: notificationsError } = useGetNotificationsQuery(
    { page: 1, limit: 5 }
  );
  const [bulkMarkAsRead, { isLoading: bulkMarkLoading }] = useBulkMarkAsReadMutation();


  const [openLogoutDialog, setOpenLogoutDialog] = useState(false);

  const handleLogoutClick = () => {
    handleClose();
    setOpenLogoutDialog(true);
  };

  const handleLogoutConfirm = async () => {
    try {
      await logoutApi({}).unwrap();
    } catch (err) {
      console.error("Logout API failed:", err);
    } finally {
      dispatch(logout());
      setOpenLogoutDialog(false);
      navigate("/");
      toast.success("Logged out successfully");
    }
  };

  const handleLogoutCancel = () => {
    setOpenLogoutDialog(false);
  };

  useEffect(() => {
    if (profileData?.data) {
      const token = localStorage.getItem('token') || "";
      dispatch(setCredentials({ user: profileData.data, token }));
    }
  }, [profileData, dispatch]);

  useEffect(() => {
    if (window) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, []);

  const [scroll, setScroll] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const [anchorEl1, setAnchorEl1] = React.useState<null | HTMLElement>(null);
  const open1 = Boolean(anchorEl1);
  
  const handleClick1 = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl1(event.currentTarget);
  };
  
  const handleClose1 = () => {
    setAnchorEl1(null);
  };

  useEffect(() => {
    window.addEventListener("scroll", () => {
      setScroll(window.scrollY > 10);
    });
  }, []);

  const handleMarkAllAsRead = async () => {
    try {
      await bulkMarkAsRead({}).unwrap();
      toast.success("All notifications marked as read");
    } catch (err) {
      console.error("Failed to mark all as read:", err);
      toast.error("Failed to mark all as read");
    }
  };


  // Format date to proper format
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const notifications = notificationsData?.data?.data || [];
  const unreadCount = notificationsData?.data?.unreadCount || 0;

  const handleNotificationClick = (notification: any) => {
    handleClose1();
    const title = notification.title?.toLowerCase() || "";
    if (title.includes("user")) {
      navigate("/manage-users");
    } else if (title.includes("car") || title.includes("vehicle") || title.includes("listed")) {
      navigate("/manage-vehicles");
    } else {
      navigate("/recieved-notifications");
    }
  };

  return (
    <Box className="site_header">
      <AppBar
        position="fixed"
        className="topbar"
        sx={{
          width: `calc(100% - ${SIDEBAR_WIDTH}px)`,
          ml: `${SIDEBAR_WIDTH}px`,
        }}
      >
        <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
          <div className="topbar_left">
            <IconButton
              onClick={handleSidebarToggle}
            >
              <MenuIcon />
            </IconButton>
          </div>
          <div className="topbar_right">
            <Box>
              <IconButton
                className="icon_btn"
                aria-controls={open1 ? "basic-menu1" : undefined}
                aria-haspopup="true"
                aria-expanded={open1 ? "true" : undefined}
                onClick={handleClick1}
              >
                <Badge badgeContent={unreadCount > 0 ? unreadCount : 0} color="error">
                  <NotificationsActiveIcon />
                </Badge>
              </IconButton>
              <Menu
                className="notiDropdown_menu"
                id="basic-menu1"
                anchorEl={anchorEl1}
                open={open1}
                onClose={handleClose1}
                MenuListProps={{
                  "aria-labelledby": "basic-button1",
                }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              >
                <Box className="noti_head">
                  <Typography component="h2">Notifications</Typography>
                  <Box sx={{ display: 'flex', gap: '8px' }}>
                    <Button
                      className="btn btn_primary sm"
                      onClick={handleMarkAllAsRead}
                      disabled={unreadCount === 0 || bulkMarkLoading}
                      sx={{
                        "&.Mui-disabled": {
                          backgroundColor: "#f5f5f5",
                          color: "#999",
                          opacity: 0.7,
                          cursor: 'not-allowed',
                          pointerEvents: 'auto'
                        }
                      }}
                    >
                      {bulkMarkLoading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : "Mark All as Read"}
                    </Button>


                    <Button
                      className="btn btn_primary sm"
                      onClick={() => { navigate("/recieved-notifications"); handleClose1() }}
                    >
                      See All
                    </Button>
                  </Box>
                </Box>

                
                {notificationsLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px' }}>
                    <CircularProgress size={30} />
                  </Box>
                )}
                
                {notificationsError && (
                  <Box sx={{ padding: '12px' }}>
                    <Alert severity="error">Failed to load notifications</Alert>
                  </Box>
                )}
                
                {!notificationsLoading && notifications.length === 0 && (
                  <Box sx={{ padding: '16px', textAlign: 'center' }}>
                    <Typography variant="body2" color="textSecondary">
                      No notifications yet
                    </Typography>
                  </Box>
                )}
                
                {!notificationsLoading && notifications.length > 0 && (
                  <ul className="noti_list">
                    {notifications.map((notification) => (
                      <li key={notification._id} onClick={() => handleNotificationClick(notification)} style={{ backgroundColor: !notification.isRead ? '#e8f0fe' : 'transparent', borderBottom: '1px solid #eee', cursor: 'pointer' }}>

                        <Box sx={{ display: 'flex', p: '12px', gap: '12px', width: '100%' }}>
                          <Box sx={{ flexShrink: 0 }}>
                            <img 
                              src={notification.user?.image || "/static/images/admin_icon.png"} 
                              alt="user" 
                              style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }} 
                            />
                          </Box>
                          <div className="noti_info" style={{ flexGrow: 1 }}>
                            <Typography component="h3" sx={{ fontWeight: !notification.isRead ? 600 : 400 }}>
                              {notification.title}
                              <Typography component="span" sx={{ float: 'right', fontSize: '11px', color: '#999' }}>{formatDate(notification.createdAt)}</Typography>
                            </Typography>
                            <Typography component="p" sx={{ fontSize: '13px', color: '#666', mt: '4px' }}>
                              {notification.message}
                            </Typography>
                          </div>
                        </Box>
                      </li>
                    ))}

                  </ul>
                )}
              </Menu>
            </Box>

            <Box>
              <IconButton
                className="topbar_user_btn"
                aria-controls={open ? "basic-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
                onClick={handleClick}
              >
                <img src={authUser?.profile || profileData?.data?.profile || "/static/images/admin_icon.png"} alt="user" style={{ borderRadius: '50%', width: '35px', height: '35px', objectFit: 'cover' }} />
              </IconButton>
              <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                  "aria-labelledby": "basic-button",
                }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              >
                <MenuItem onClick={() => { navigate("/manage-profile"); handleClose() }}>
                  <PersonIcon /> My Profile
                </MenuItem>
                <MenuItem onClick={() => { navigate("/manage-profile/change-password"); handleClose() }}>
                  <LockIcon /> Change Password
                </MenuItem>
                <MenuItem onClick={handleLogoutClick}>
                  <LogoutIcon /> Logout
                </MenuItem>
              </Menu>
            </Box>
          </div>
        </Toolbar>
      </AppBar>

      <Dialog
        open={openLogoutDialog}
        onClose={handleLogoutCancel}
        aria-labelledby="logout-dialog-title"
        aria-describedby="logout-dialog-description"
      >
        <DialogTitle id="logout-dialog-title">
          {"Confirm Logout"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="logout-dialog-description">
            Are you sure you want to logout?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: '16px' }}>
          <Button onClick={handleLogoutCancel} className="btn" sx={{ color: '#000' }}>
            No
          </Button>
          <Button onClick={handleLogoutConfirm} autoFocus className="btn btn_primary">
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Topbar;
